import TiptapHeading, { HeadingOptions } from '@tiptap/extension-heading';
import { Editor, ReactNodeViewRenderer } from '@tiptap/react';

import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorState, Transaction } from '@tiptap/pm/state';
import type { Node as PMNode } from '@tiptap/pm/model';
import { MutableRefObject } from 'react';
import { TextSelection } from '@tiptap/pm/state';
import { TDocumentConfig, THeading } from '../../../types';
import getDocumentConfig from '../../../helpers/getDocumentConfig.helper';
import { markSystemTr } from '../../plugins/shared';
import Component from './Component';
import { numberHeadings, type THeadingState, type TTreeHeading } from './headingNumbering';

export { numberHeadings, HEADING_NODE_NAME } from './headingNumbering';
export type { TTreeHeading, THeadingState, THeadingNumbering, THeadingNumberingPatch } from './headingNumbering';
import { ETiptapSelectorKey } from '@modules/documents/types/tiptap.types';
import { TGateState, diffBounds } from '../../plugins/shared';

const refData = { h: 0 };

// ─────────────────────────────────────────────────────────────────────────────
// Heading-specific gate (numbering attr is NOT stored in HTML)
// ─────────────────────────────────────────────────────────────────────────────

const META_HEADING_NUMBERING = 'headingNumbering';
const META_FORCE_HEADING_UPDATE = 'forceHeadingUpdate';

/** Collect heading levels and text in a range for comparison. */
const collectHeadingData = (doc: PMNode, from: number, to: number): Array<{ level: number; text: string }> => {
  const data: Array<{ level: number; text: string }> = [];
  doc.nodesBetween(from, to, (n) => {
    if (n.type.name === ETiptapSelectorKey.HEADING) {
      data.push({ level: n.attrs.level as number, text: n.textContent.trim() });
      return false;
    }
    if (n.isTextblock) return false;
    return true;
  });
  return data;
};

/** Check if diff region contains any heading. */
const hasHeadingInRange = (doc: PMNode, from: number, to: number): boolean => {
  let found = false;
  doc.nodesBetween(from, Math.min(to, doc.content.size), (n) => {
    if (n.type.name === ETiptapSelectorKey.HEADING) {
      found = true;
      return false;
    }
    if (n.isTextblock) return false;
    return !found;
  });
  return found;
};

/**
 * Gate for heading numbering. Returns NEW object to trigger appendTransaction,
 * or SAME object to skip. Triggers when headings are structurally affected.
 */
const applyHeadingGate = (
  tr: Transaction,
  value: TGateState,
  oldState: EditorState,
  newState: EditorState
): TGateState => {
  if (!tr.docChanged && value.updatedTime >= 0) return value;

  const diff = diffBounds(oldState, newState);
  if (!diff) return value;

  const oldHasHeading = hasHeadingInRange(oldState.doc, diff.start, diff.oldEnd);
  const newHasHeading = hasHeadingInRange(newState.doc, diff.start, diff.newEnd);

  if (oldHasHeading || newHasHeading) {
    const oldData = collectHeadingData(oldState.doc, diff.start, diff.oldEnd);
    const newData = collectHeadingData(newState.doc, diff.start, diff.newEnd);

    // Trigger if heading count changed
    if (oldData.length !== newData.length) return { updatedTime: Date.now() };

    // Trigger if heading levels or text changed (text changes update TOC)
    for (let i = 0; i < oldData.length; i++) {
      if (oldData[i].level !== newData[i].level || oldData[i].text !== newData[i].text) {
        return { updatedTime: Date.now() };
      }
    }

    // Check for uninitialized headings in diff region (handles mode switch with content reload)
    let hasUninitialized = false;
    newState.doc.nodesBetween(diff.start, diff.newEnd, (n) => {
      if (n.type.name === ETiptapSelectorKey.HEADING && n.attrs.numbering == null) {
        hasUninitialized = true;
        return false;
      }
      if (n.isTextblock) return false;
      return !hasUninitialized;
    });
    if (hasUninitialized) return { updatedTime: Date.now() };
  }

  return value;
};

interface ExtendedHeadingOptions extends HeadingOptions {
  refEditor: MutableRefObject<Editor | null>;
  refConfig: MutableRefObject<TDocumentConfig | undefined>;
  onUpdate?: (headings: TTreeHeading[]) => void;
  /**
   * Counter state for the headings preceding the part the editor holds, when a large document is
   * loaded in parts. Without it every part restarts numbering at 1 in the middle of the document.
   */
  headingSeed?: (headingStart: number) => THeadingState | null;
  /**
   * The outline for the WHOLE document, given the editor's current part. Without it the outline panel
   * and the saved `headings` array cover only that part - and the saved array is REPLACED on save.
   */
  headingTree?: (headingStart: number, liveDoc: PMNode) => TTreeHeading[] | null;
}

const key = new PluginKey<TGateState>('heading-numbering');
const getTrackHeadingPlugin = (options: ExtendedHeadingOptions) =>
  new Plugin<TGateState>({
    key,

    state: {
      init(): TGateState {
        return { updatedTime: -1 };
      },

      apply(tr, value, oldState, newState) {
        if (tr.getMeta(META_HEADING_NUMBERING)) return value;
        // Force update when config changes (e.g., headingStart)
        if (tr.getMeta(META_FORCE_HEADING_UPDATE)) {
          return { updatedTime: Date.now() };
        }
        return applyHeadingGate(tr, value, oldState, newState);
      },
    },

    appendTransaction(transactions, oldState, newState) {
      // @ts-expect-error – access plugin state snapshots
      if (oldState[key.key] === newState[key.key]) return null;
      if (transactions.some((tr) => tr.getMeta(META_HEADING_NUMBERING))) return null;

      let transaction = newState.tr;

      const { headingStart } = getDocumentConfig(options.refConfig.current);
      const seed = options.headingSeed?.(headingStart) ?? undefined;
      const { tree, patches, count } = numberHeadings(newState.doc, headingStart, seed);

      for (const patch of patches) {
        transaction = transaction.setNodeMarkup(patch.pos, undefined, { ...patch.attrs, numbering: patch.numbering });
      }
      const modified = patches.length > 0;

      if (options.onUpdate) options.onUpdate(options.headingTree?.(headingStart, newState.doc) ?? tree);
      refData.h = count;

      if (modified) {
        transaction.setMeta(META_HEADING_NUMBERING, true);
        return markSystemTr(transaction);
      }
      return null;
    },
  });

const HeadingNode = TiptapHeading.extend<ExtendedHeadingOptions>({
  marks: '',
  content: 'text*',

  addAttributes() {
    return {
      ...this.parent?.(),
      numbering: { default: null, rendered: false },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(Component, { className: 'relative' });
  },

  addProseMirrorPlugins() {
    return [
      getTrackHeadingPlugin(this.options),
      new Plugin({
        props: {
          handleDOMEvents: {
            dragstart(view) {
              const { state, dispatch } = view;
              const { doc, selection } = view.state;
              const { from, to } = selection;

              let minFrom = from;
              let maxTo = to;
              doc.nodesBetween(from, to, (node, pos) => {
                if (node.type.name === ETiptapSelectorKey.HEADING) {
                  minFrom = Math.min(minFrom, pos);
                  maxTo = Math.max(maxTo, pos + node.nodeSize);
                }
              });
              if (minFrom !== from || maxTo !== to) {
                const tr = state.tr.setSelection(TextSelection.create(state.doc, minFrom, maxTo));
                dispatch(tr);
              }
            },
          },
        },
      }),
    ];
  },
});

export default HeadingNode;
